import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import deskGlass from "../../styles/deskGlass";

const styles = deskGlass;

/**
 * Horizontal snap swiper — dashboard centerpiece pattern.
 */
const DeskSwiper = forwardRef(function DeskSwiper(
  {
    slides = [],
    initial = 0,
    onSlideChange,
    scrollDisabled = false,
    showIndicators = true,
    showNavHints = false,
  },
  ref
) {
  const [activeSlide, setActiveSlide] = useState(initial);
  const swiperRef = useRef(null);
  const count = slides.length;

  const scrollToSlide = useCallback(
    (index) => {
      const clamped = Math.max(0, Math.min(count - 1, index));
      setActiveSlide(clamped);
      onSlideChange?.(clamped);
      if (swiperRef.current && swiperRef.current.offsetWidth > 0) {
        swiperRef.current.scrollTo({
          left: clamped * swiperRef.current.offsetWidth,
          behavior: "smooth",
        });
      }
      return clamped;
    },
    [count, onSlideChange]
  );

  useImperativeHandle(ref, () => ({ scrollToSlide, activeSlide }), [scrollToSlide, activeSlide]);

  const onSwiperScroll = (e) => {
    const slideWidth = e.target.offsetWidth;
    if (!slideWidth) return;
    const newSlide = Math.round(e.target.scrollLeft / slideWidth);
    if (newSlide !== activeSlide) {
      setActiveSlide(newSlide);
      onSlideChange?.(newSlide);
    }
  };

  const handleSwiperWheel = (e) => {
    if (!e.shiftKey) return;
    e.preventDefault();
    if (e.deltaY > 20) scrollToSlide(activeSlide + 1);
    else if (e.deltaY < -20) scrollToSlide(activeSlide - 1);
  };

  useEffect(() => {
    const timer = setTimeout(() => scrollToSlide(initial), 150);
    return () => clearTimeout(timer);
  }, [initial, scrollToSlide]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollToSlide(activeSlide + 1);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        scrollToSlide(activeSlide - 1);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeSlide, scrollToSlide]);

  if (!count) return null;

  return (
    <>
      {showIndicators && count > 1 && (
        <div style={styles.indicatorContainer}>
          {slides.map((s, i) => (
            <button
              key={s.label}
              type="button"
              onClick={() => scrollToSlide(i)}
              title={s.label}
              aria-label={`Ir a ${s.label}`}
              aria-current={activeSlide === i ? "true" : undefined}
              style={{
                ...styles.indicatorBtn,
                backgroundColor: activeSlide === i ? "#fff" : "rgba(255,255,255,0.2)",
                width: activeSlide === i ? "20px" : "6px",
              }}
            />
          ))}
        </div>
      )}

      <div
        className="desk-swiper"
        style={{
          ...styles.swiper,
          overflowX: scrollDisabled ? "hidden" : "auto",
          scrollSnapType: scrollDisabled ? "none" : "x mandatory",
        }}
        onScroll={onSwiperScroll}
        onWheel={handleSwiperWheel}
        ref={swiperRef}
      >
        {slides.map((s) => (
          <div key={s.label} style={styles.slide}>
            {s.content}
          </div>
        ))}
      </div>

      {showNavHints && count > 1 && (
        <div
          style={{
            position: "fixed",
            bottom: "130px",
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            gap: "12px",
            alignItems: "center",
            zIndex: 99,
            pointerEvents: "auto",
          }}
        >
          <button
            type="button"
            onClick={() => scrollToSlide(activeSlide - 1)}
            disabled={activeSlide === 0}
            style={styles.slideNavBtn}
          >
            Anterior
          </button>
          <span style={{ opacity: 0.5, fontSize: "0.8rem" }}>
            {slides[activeSlide]?.label}
          </span>
          <button
            type="button"
            onClick={() => scrollToSlide(activeSlide + 1)}
            disabled={activeSlide === count - 1}
            style={styles.slideNavBtn}
          >
            Siguiente
          </button>
        </div>
      )}

      <style>{`
        .desk-swiper {
          display: flex;
          overflow-x: auto;
          scroll-snap-type: x mandatory;
          -webkit-overflow-scrolling: touch;
          height: 100%;
          scroll-behavior: smooth;
        }
        .desk-swiper::-webkit-scrollbar { display: none; }
      `}</style>
    </>
  );
});

export default DeskSwiper;
